import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChartLine, faChartBar } from '@/fontawesome';
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { APIRoutes } from '@/services/api.routes';
import { JOURNAL_ROUTES } from '@/constants/routes';
import { formatCurrencyCentValue, formatRFCDate, formatPercentage } from '@/utils/helpers';
import type { JournalOptionTrade } from '@/models';
import { STRATEGY_LABELS, OPTION_STATUS_LABELS, optionStatusBadgeClass, pnlColorClass } from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';
import JournalAiSummaryCard from '@/components/journal/JournalAiSummaryCard';
import { useDarkMode } from '@/hooks/useDarkMode';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import DataTable from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import EmptyState from '@/components/ui/EmptyState';
import ErrorState from '@/components/ui/ErrorState';
import StatCard from '@/components/ui/StatCard';

const AXIS_CURRENCY_FORMAT = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const TOOLTIP_CURRENCY_FORMAT = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

const POSITIVE_BAR_COLOR = '#16a34a';
const NEGATIVE_BAR_COLOR = '#dc2626';

const RECENT_TRADE_COLUMNS: DataTableColumn<JournalOptionTrade>[] = [
  { key: 'symbol', header: 'Symbol', primary: true, render: (t) => <span className="font-medium text-gray-900 dark:text-white">{t.symbolTicker}</span> },
  { key: 'right', header: 'Typ', render: (t) => t.right },
  { key: 'strike', header: 'Strike', render: (t) => formatCurrencyCentValue(t.strike, t.currency) },
  { key: 'expiry', header: 'Verfall', render: (t) => formatRFCDate(t.expiry) },
  { key: 'strategy', header: 'Strategie', render: (t) => STRATEGY_LABELS[t.strategy] },
  {
    key: 'status',
    header: 'Status',
    render: (t) => <Badge colorClass={optionStatusBadgeClass(t.status)}>{OPTION_STATUS_LABELS[t.status]}</Badge>,
  },
  {
    key: 'realizedPnL',
    header: 'Realisiert',
    align: 'right',
    render: (t) => (
      <span className={`font-medium ${pnlColorClass(t.realizedPnL)}`}>
        {t.realizedPnL === null ? '—' : formatCurrencyCentValue(t.realizedPnL, t.currency)}
      </span>
    ),
  },
];

export default function JournalDashboard() {
  const { isDarkMode } = useDarkMode();
  const { data, loading, error, reload } = useJournalResource(
    async () => {
      const [statsResp, tradesResp] = await Promise.all([
        APIRoutes.getJournalStats(),
        APIRoutes.listJournalOptionTrades({ limit: 5 }),
      ]);
      return { stats: statsResp, recentTrades: tradesResp.optionTrades };
    },
    [],
    'Fehler beim Laden des Trading Journals',
  );

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  if (loading) {
    return (
      <div className="text-center py-16" role="status" aria-label="Lädt">
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  const stats = data?.stats ?? null;
  const recentTrades = data?.recentTrades ?? [];
  const weekChartData = (stats?.byWeek ?? []).map((w) => ({
    period: w.period,
    pnl: w.realizedPnL / 100,
  }));

  const axisColor = isDarkMode ? '#9ca3af' : '#6b7280';
  const gridColor = isDarkMode ? '#374151' : '#e5e7eb';

  return (
    <div className="space-y-6">
      {stats !== null && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <StatCard label="Realisiert gesamt" value={formatCurrencyCentValue(stats.realizedPnLTotal, 'USD')} valueClassName={pnlColorClass(stats.realizedPnLTotal)} />
            <StatCard label="Diese Woche" value={formatCurrencyCentValue(stats.realizedPnLThisWeek, 'USD')} valueClassName={pnlColorClass(stats.realizedPnLThisWeek)} />
            <StatCard label="Dieser Monat" value={formatCurrencyCentValue(stats.realizedPnLThisMonth, 'USD')} valueClassName={pnlColorClass(stats.realizedPnLThisMonth)} />
            <StatCard label="Unrealisiert (offen)" value={formatCurrencyCentValue(stats.unrealizedPnLTotal, 'USD')} valueClassName={pnlColorClass(stats.unrealizedPnLTotal)} />
            <StatCard label="Offene Optionen" value={String(stats.openOptionCount)} />
            <StatCard label="Gebundenes Kapital" value={formatCurrencyCentValue(stats.capitalAtRisk, 'USD')} />
            <StatCard label="Trefferquote" value={formatPercentage(stats.winRate)} />
          </div>

          <Card title="Realisierte Gewinne pro Woche">
            {weekChartData.length === 0 ? (
              <EmptyState
                icon={faChartBar}
                title="Noch keine abgeschlossenen Trades"
                description="Sobald Trades geschlossen sind, erscheint hier die Wochenbilanz."
              />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={weekChartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                  <XAxis dataKey="period" stroke={axisColor} tick={{ fontSize: 11, fill: axisColor }} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis
                    stroke={axisColor}
                    tick={{ fontSize: 11, fill: axisColor }}
                    tickFormatter={(v: number) => AXIS_CURRENCY_FORMAT.format(v)}
                  />
                  <Tooltip
                    formatter={(v: number) => TOOLTIP_CURRENCY_FORMAT.format(v)}
                    cursor={{ fill: isDarkMode ? '#37415155' : '#f3f4f6' }}
                    contentStyle={{
                      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                      border: `1px solid ${gridColor}`,
                      borderRadius: '0.5rem',
                    }}
                    itemStyle={{ color: isDarkMode ? '#f9fafb' : '#111827' }}
                    labelStyle={{ color: isDarkMode ? '#d1d5db' : '#374151' }}
                  />
                  <Bar dataKey="pnl" name="Realisiert" radius={[4, 4, 0, 0]}>
                    {weekChartData.map((w) => (
                      <Cell key={w.period} fill={w.pnl >= 0 ? POSITIVE_BAR_COLOR : NEGATIVE_BAR_COLOR} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </>
      )}

      <JournalAiSummaryCard />

      <Card
        title="Letzte Trades"
        padded={false}
        actions={<Button variant="ghost" size="sm" to={JOURNAL_ROUTES.TRADES}>Alle anzeigen</Button>}
      >
        <DataTable
          bare
          columns={RECENT_TRADE_COLUMNS}
          rows={recentTrades}
          keyExtractor={(t) => t.id}
          rowHref={(t) => JOURNAL_ROUTES.TRADE_DETAIL(t.id)}
          emptyState={
            <EmptyState
              icon={faChartLine}
              title="Noch keine Trades erfasst"
              description="Lege deinen ersten Optionstrade an, um ihn hier zu sehen."
              action={<Button size="sm" to={JOURNAL_ROUTES.TRADES}>Zu den Trades</Button>}
            />
          }
        />
      </Card>
    </div>
  );
}
